<script setup lang="ts">
import { computed } from 'vue'
import i18n from '@/plugins/translationsPlugin'
import {
  NXGDSStatusTag,
  NXGDSStatusTagVariants,
  NXGDSTagIndicator,
  NXGDSTagIndicatorVariants,
} from '@nsftx/seven-nxg-design-system'
import type { Bet } from '@nsftx/seven-client-retail-vue-integrator'
import SSDetailsTable from '@/ui/components/common/SSDetailsTable.vue'
import {
  DetailsTableColumnAlign,
  DetailsTableFooterRowEmphasis,
  DetailsTableFooterRowTone,
  type DetailsTableColumn,
  type DetailsTableFooter,
  type DetailsTableRow,
} from '@/types/SSDetailsTableProps'
import { useBetslip } from '@nsftx/seven-client-retail-vue-sgp'
import { formatNumber } from '@nsftx/seven-nxg-retail-vue-sdk'
import { formatDateTimeToLocaleString } from '@/utils/dateTimeHelpers'

const { t } = i18n.global

const { bet } = defineProps<{
  bet: Bet
}>()

const { betslipCurrency } = useBetslip()

const formatBetAmount = (amount: number) =>
  formatNumber(amount, {
    style: 'currency',
    currencySymbol: betslipCurrency.value ?? '',
  })

const columns: DetailsTableColumn[] = [
  { key: 'status', labelKey: 'bet_details_header_status' },
  { key: 'event', labelKey: 'bet_details_header_event' },
  { key: 'banker', modifier: 'banker' },
  { key: 'time', labelKey: 'bet_details_header_time' },
  { key: 'market', labelKey: 'bet_details_header_market' },
  { key: 'outcome', labelKey: 'bet_details_header_outcome' },
  { key: 'odds', labelKey: 'bet_details_header_odds', align: DetailsTableColumnAlign.right },
]

const isBetBuilderBet = computed(() => bet.selections[0].markets.length > 1)

const mapStandardRows = (selections: Bet['selections']): DetailsTableRow[] =>
  selections.map((selection) => ({
    status: selection.status,
    event: selection.event.derivedName,
    isBanker: selection.isBanker,
    time: formatDateTimeToLocaleString(selection.event.startTime),
    market: selection.markets[0].derivedName,
    outcome: selection.markets[0].outcome.derivedName,
    odds: selection.odds / 10000,
  }))

const mapBetBuilderRows = (selection: Bet['selections'][number]): DetailsTableRow[] => {
  const headerRow: DetailsTableRow = {
    status: selection.status,
    event: selection.event.derivedName,
    isBanker: selection.isBanker,
    time: formatDateTimeToLocaleString(selection.event.startTime),
    market: t('bet_builder'),
    outcome: '-',
    odds: selection.odds / 10000,
  }

  const bodyRows = selection.markets.map((market) => ({
    status: selection.status,
    event: '',
    isBanker: false,
    time: '',
    market: market.derivedName,
    outcome: market.outcome.derivedName,
    odds: undefined,
  }))

  return [headerRow, ...bodyRows]
}

const rows = computed(() =>
  isBetBuilderBet.value ? mapBetBuilderRows(bet.selections[0]) : mapStandardRows(bet.selections),
)

const footer = computed<DetailsTableFooter>(() => ({
  summaryRows: [
    {
      labelKey: 'bet_details_payment',
      value: formatBetAmount(Number(bet.payin.total)),
      emphasis: DetailsTableFooterRowEmphasis.medium,
      tone: DetailsTableFooterRowTone.primary,
    },
    {
      labelKey: 'bet_details_possible_payout',
      value: formatBetAmount(Number(bet.pWinnings.max.total)),
      emphasis: DetailsTableFooterRowEmphasis.medium,
      tone: DetailsTableFooterRowTone.highlight,
    },
  ],
  expandableRows: [
    {
      labelKey: 'bet_details_stake',
      value: formatBetAmount(Number(bet.payin.stake)),
      emphasis: DetailsTableFooterRowEmphasis.regular,
      tone: DetailsTableFooterRowTone.secondary,
    },
    {
      labelKey: 'bet_details_payment_tax',
      value: formatBetAmount(0),
      emphasis: DetailsTableFooterRowEmphasis.regular,
      tone: DetailsTableFooterRowTone.secondary,
    },
    {
      labelKey: 'bet_details_possible_winnings',
      value: formatBetAmount(Number(bet.pWinnings.max.total)),
      emphasis: DetailsTableFooterRowEmphasis.regular,
      tone: DetailsTableFooterRowTone.secondary,
    },
    {
      labelKey: 'bet_details_tax_on_winnings',
      value: formatBetAmount(0),
      emphasis: DetailsTableFooterRowEmphasis.regular,
      tone: DetailsTableFooterRowTone.secondary,
    },
  ],
}))

const getStatusVariant = (status: string) => {
  switch (status) {
    case 'WON':
      return NXGDSStatusTagVariants.positive
    case 'LOST':
      return NXGDSStatusTagVariants.negative
  }
  return NXGDSStatusTagVariants.attention
}
</script>

<template>
  <SSDetailsTable :columns="columns" :rows="rows" :footer="footer">
    <template #cell-status="{ row }">
      <NXGDSStatusTag
        :label="row.status as string"
        :variant="getStatusVariant(row.status as string)"
      />
    </template>
    <template #cell-banker="{ row }">
      <NXGDSTagIndicator
        v-if="row.isBanker"
        label="B"
        :variant="NXGDSTagIndicatorVariants.subtle"
      />
    </template>
  </SSDetailsTable>
</template>
